import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HomeIcon, Bars3Icon } from '@heroicons/react/24/outline'
import { useAuthStore } from '@/stores/auth.store'
import { getPrimaryNav, buildNavCtx } from '@/config/navModel'

interface Props { onMenuClick: () => void; overdueCount?: number }

const navItemBase = 'flex-1 flex flex-col items-center justify-start gap-1 pt-2.5 pb-2 transition-colors min-w-0 relative'
const navItemActive = 'text-cyan-400'
const navItemInactive = 'text-white/50 hover:text-white/80 active:text-white'

export default function BottomNav({ onMenuClick, overdueCount = 0 }: Props) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const profileType   = useAuthStore(s => s.profileType)
  const hasCapability = useAuthStore(s => s.hasCapability)
  const isAdmin       = useAuthStore(s => s.isAdminUser)
  const isSales       = useAuthStore(s => s.isSalesUser)
  const user          = useAuthStore(s => s.user)

  // Stessa sorgente della sidebar: le voci primarie del ruolo (UX-01 §4).
  const ctx = buildNavCtx({ profileType: profileType ?? null, isAdmin, isSales, hasCapability, roles: user?.roles })
  const tabs = getPrimaryNav(ctx, t)

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-gray-900 border-t border-white/10 flex"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Dashboard — sempre primo */}
      <NavLink
        to="/app/dashboard"
        end
        className={({ isActive }) => `${navItemBase} ${isActive ? navItemActive : navItemInactive}`}
      >
        {({ isActive }) => (
          <>
            <span className="relative">
              <HomeIcon className="w-6 h-6" aria-hidden="true" />
              {overdueCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-400 rounded-full border-2 border-gray-900" />
              )}
            </span>
            <span className="text-[10px] tracking-tight font-medium leading-tight w-full text-center px-0.5 [overflow-wrap:anywhere]">{t('auto.componentsBottomNav.home')}</span>
            {isActive && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-cyan-400 rounded-full" />}
          </>
        )}
      </NavLink>

      {tabs.map(({ to, label, icon: Icon, match }) => {
        const extraActive = !!match?.some(p => pathname === p || pathname.startsWith(p + '/'))
        return (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => `${navItemBase} ${isActive || extraActive ? navItemActive : navItemInactive}`}
          >
            {({ isActive }) => (
              <>
                <Icon className="w-6 h-6" aria-hidden="true" />
                <span className="text-[10px] tracking-tight font-medium leading-tight w-full text-center px-0.5 [overflow-wrap:anywhere]">{label}</span>
                {(isActive || extraActive) && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-cyan-400 rounded-full" />}
              </>
            )}
          </NavLink>
        )
      })}

      {/* Altro — apre la sidebar completa */}
      <button
        onClick={onMenuClick}
        aria-label={t('nav.more')}
        className={`${navItemBase} ${navItemInactive}`}
      >
        <Bars3Icon className="w-6 h-6" aria-hidden="true" />
        <span className="text-xs font-medium leading-none">{t('nav.more')}</span>
      </button>
    </nav>
  )
}
